import type { CSSProperties } from 'react';

/**
 * Skeleton — loading 中の placeholder (Sprint BG-DataSettings 21、design system 補完)。
 *
 * 旧 v1 では PostsList / DistrictList 等で independent skeleton 風 markup を inline 直書き
 * していた (col-pulse な div + Tailwind animate-pulse)。v2 では shared-ui で集約する。
 *
 * variant:
 *   - rect: 矩形 (画像 / カード本体)
 *   - circle: 円形 (avatar / icon)
 *   - text: 行テキスト (height 既定 0.75em 相当 = 12px)
 *
 * a11y:
 *   - aria-hidden="true" + role="presentation" — SR には Spinner / 親側の aria-busy を使わせる
 *   - 上位 (例: <div role="status" aria-busy>) で「読込中…」を SR 通知してもらう設計
 *
 * 注意:
 *   - width / height は inline style で受け取る (Tailwind の任意値 class より柔軟)
 *   - count > 1 で連続 placeholder を生成 (text variant の段落表現用)
 */

export type SkeletonVariant = 'rect' | 'circle' | 'text';

export interface SkeletonProps {
  variant?: SkeletonVariant;
  /** css width。number は px、string はそのまま。default: '100%'。 */
  width?: number | string;
  /** css height。number は px、string はそのまま。default は variant により異なる。 */
  height?: number | string;
  /** 繰り返し個数 (text variant の段落表現)。default 1。 */
  count?: number;
  className?: string;
}

const VARIANT_BASE_CLASS = 'bg-slate-200 animate-pulse';
const VARIANT_SHAPE: Record<SkeletonVariant, string> = {
  rect: 'rounded-md',
  circle: 'rounded-full',
  text: 'rounded',
};

const DEFAULT_HEIGHT: Record<SkeletonVariant, string> = {
  rect: '1rem',
  circle: '2.5rem',
  text: '0.875rem',
};

function toCssSize(v: number | string | undefined, fallback: string): string {
  if (v === undefined) return fallback;
  return typeof v === 'number' ? `${v}px` : v;
}

export function Skeleton({
  variant = 'rect',
  width,
  height,
  count = 1,
  className = '',
}: SkeletonProps) {
  const safeCount = Math.max(1, Math.floor(count));
  const style: CSSProperties = {
    width: toCssSize(width, variant === 'circle' ? '2.5rem' : '100%'),
    height: toCssSize(height, DEFAULT_HEIGHT[variant]),
  };
  if (safeCount === 1) {
    return (
      <span
        aria-hidden="true"
        role="presentation"
        data-testid="skeleton"
        data-variant={variant}
        className={`block ${VARIANT_BASE_CLASS} ${VARIANT_SHAPE[variant]} ${className}`.trim()}
        style={style}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      role="presentation"
      data-testid="skeleton-group"
      data-variant={variant}
      className={`flex flex-col gap-1.5 ${className}`.trim()}
    >
      {Array.from({ length: safeCount }, (_, i) => (
        <span
          key={i}
          data-testid="skeleton"
          data-variant={variant}
          className={`block ${VARIANT_BASE_CLASS} ${VARIANT_SHAPE[variant]}`}
          style={style}
        />
      ))}
    </span>
  );
}
